
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { LucideIcon } from 'lucide-react';
import { TranslationKey } from '@/utils/i18n';
import { 
  SidebarGroup, 
  SidebarGroupLabel, 
  SidebarGroupContent, 
  SidebarMenu, 
  SidebarMenuItem, 
  SidebarMenuButton 
} from '@/components/ui/sidebar';

interface MenuItem {
  path: string;
  labelKey: string | TranslationKey;
  icon: LucideIcon;
  state?: { activeTab: string };
}

interface SidebarMenuGroupProps {
  labelKey: string | TranslationKey;
  items: MenuItem[];
}

export const SidebarMenuGroup = ({ labelKey, items }: SidebarMenuGroupProps) => {
  const { t } = useLanguage();
  const location = useLocation();

  const isActive = (path: string, state?: { activeTab: string }) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    
    // Check if the path matches the current location
    const pathMatches = location.pathname.startsWith(path);
    
    // For tab-specific paths, check if we are already on that tab
    if (state?.activeTab && location.pathname === '/creator/panel') {
      const urlParams = new URLSearchParams(location.search);
      const currentTab = urlParams.get('tab') || 'profile';
      return currentTab === state.activeTab;
    }
    
    return pathMatches;
  };
  
  const getItemTo = (item: MenuItem) => {
    // For items that need to navigate to specific tabs, use search params
    if (item.state?.activeTab) {
      return {
        pathname: item.path,
        search: `?tab=${item.state.activeTab}`
      };
    }
    return item.path;
  };

  return (
    <SidebarGroup>
      <SidebarGroupLabel>{t(labelKey)}</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => {
            const active = isActive(item.path, item.state);
            return (
              <SidebarMenuItem key={item.path + (item.state?.activeTab || '')}>
                <SidebarMenuButton 
                  asChild 
                  isActive={active}
                  tooltip={t(item.labelKey)}
                  className={active ? 'opacity-100' : 'opacity-90'}
                >
                  <Link 
                    to={getItemTo(item)}
                    className={`flex items-center ${active ? 'opacity-100' : 'opacity-90'}`}
                  >
                    <item.icon className={`h-5 w-5 mr-2 ${active ? 'opacity-100' : 'opacity-90'}`} />
                    <span className={active ? 'opacity-100' : 'opacity-90'}>{t(item.labelKey)}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
};

